import { useEffect, useMemo, useState } from "react";
import { FileText, Send, Activity, Plus, X, Search, Heart, AlertTriangle, Users as UsersIcon, Pill, Save, Edit3, Calculator, ClipboardList, Stethoscope, FileSignature, FileCheck, Download, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { Link, useLocation } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { generateMedicalReportPDF } from "../lib/medicalReportPdf";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { FileUploader, AttachmentList } from "../components/FileUploader";

/* ============ BMI helper ============ */
function computeBMI(weight, height) {
  if (!weight || !height) return null;
  const h = height / 100;
  const bmi = weight / (h * h);
  if (!isFinite(bmi) || bmi <= 0) return null;
  return bmi;
}
function bmiLabel(bmi) {
  if (bmi == null) return null;
  if (bmi < 18.5) return { text: "Bajo peso", color: "text-blue-700 bg-blue-50 border-blue-200" };
  if (bmi < 25)   return { text: "Normal",    color: "text-emerald-700 bg-emerald-50 border-emerald-200" };
  if (bmi < 30)   return { text: "Sobrepeso", color: "text-amber-700 bg-amber-50 border-amber-200" };
  if (bmi < 35)   return { text: "Obesidad I", color: "text-orange-700 bg-orange-50 border-orange-200" };
  if (bmi < 40)   return { text: "Obesidad II", color: "text-rose-700 bg-rose-50 border-rose-200" };
  return { text: "Obesidad III", color: "text-red-700 bg-red-50 border-red-200" };
}

/* ============ Vitals form (with auto-BMI) ============ */
function VitalsForm({ vitals, onChange }) {
  const set = (k, v) => onChange({
    ...vitals,
    [k]: v === "" ? null : (["ta_sys", "ta_dia", "fc", "fr", "sat_o2"].includes(k) ? parseInt(v) : parseFloat(v))
  });
  const fields = [
    { k: "ta_sys", label: "TA Sistólica (mmHg)", ph: "120" },
    { k: "ta_dia", label: "TA Diastólica (mmHg)", ph: "80" },
    { k: "fc", label: "FC (lpm)", ph: "72" },
    { k: "fr", label: "FR (rpm)", ph: "16" },
    { k: "temp", label: "Temp (°C)", ph: "36.5" },
    { k: "sat_o2", label: "SatO₂ (%)", ph: "98" },
    { k: "weight", label: "Peso (kg)", ph: "70" },
    { k: "height", label: "Talla (cm)", ph: "170" },
  ];
  const bmi = computeBMI(vitals.weight, vitals.height);
  const label = bmiLabel(bmi);
  return (
    <div data-testid="vitals-form">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {fields.map((f) => (
          <div key={f.k}>
            <Label className="text-xs">{f.label}</Label>
            <Input type="number" step="0.1" value={vitals[f.k] ?? ""} onChange={(e) => set(f.k, e.target.value)} placeholder={f.ph} data-testid={`vital-${f.k}`} />
          </div>
        ))}
      </div>
      {bmi != null && (
        <div className={`mt-2 flex items-center gap-2 text-xs px-3 py-2 rounded-lg border ${label.color}`} data-testid="bmi-badge">
          <Calculator className="w-4 h-4" />
          <span className="font-semibold">IMC {bmi.toFixed(1)}</span>
          <span className="opacity-80">— {label.text}</span>
        </div>
      )}
    </div>
  );
}

/* ============ ICD-10 picker (unchanged) ============ */
function ICD10Picker({ selected, onChange }) {
  const [catalog, setCatalog] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/catalog/icd10").then(({ data }) => setCatalog(data)); }, []);
  const filtered = catalog.filter((c) => !q || c.code.toLowerCase().includes(q.toLowerCase()) || c.label.toLowerCase().includes(q.toLowerCase())).slice(0, 8);
  const toggle = (c) => onChange(selected.includes(c.code) ? selected.filter((x) => x !== c.code) : [...selected, c.code]);
  return (
    <div data-testid="icd10-picker">
      <Input placeholder="Buscar CIE-10 por código o síntoma..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="icd10-search" />
      {q && (
        <div className="mt-2 border rounded-lg max-h-40 overflow-y-auto bg-white">
          {filtered.map((c) => (
            <button key={c.code} type="button" onClick={() => toggle(c)} className={`block w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 ${selected.includes(c.code) ? "bg-emerald-100" : ""}`} data-testid={`icd10-${c.code}`}>
              <span className="font-mono font-semibold text-emerald-700 mr-2">{c.code}</span>{c.label}
            </button>
          ))}
        </div>
      )}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selected.map((code) => {
            const c = catalog.find((x) => x.code === code);
            return (
              <Badge key={code} variant="outline" className="pl-2 pr-1 gap-1">
                <span className="font-mono">{code}</span>
                {c && <span className="text-xs text-muted-foreground truncate max-w-[200px]">{c.label}</span>}
                <button type="button" onClick={() => toggle({ code })} className="ml-1 hover:text-red-600"><X className="w-3 h-3" /></button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProfileField({ icon: Ic, label, field, testid, edit, profile, setProfile }) {
  return (
    <div className="border-t border-border pt-2 mt-2 first:border-t-0 first:pt-0 first:mt-0">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-1">
        <Ic className="w-3.5 h-3.5" /> {label}
      </div>
      {edit ? (
        <Textarea rows={2} value={profile[field] || ""} onChange={(e) => setProfile({ ...profile, [field]: e.target.value })} data-testid={`profile-${testid}`} className="text-sm" />
      ) : (
        <div className="text-sm text-foreground/90 whitespace-pre-wrap min-h-[1.5rem]" data-testid={`profile-view-${testid}`}>
          {profile[field] || <span className="text-muted-foreground italic">—</span>}
        </div>
      )}
    </div>
  );
}

/* ============ Medical profile side panel (allergies, blood, family) ============ */
function MedicalProfilePanel({ patient, onSaved }) {
  const [profile, setProfile] = useState({});
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!patient) return;
    api.get(`/patients/${patient.id}/medical-profile`)
      .then(({ data }) => setProfile(data || {}))
      .catch(() => setProfile({}));
    setEdit(false);
  }, [patient?.id]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        blood_type: profile.blood_type || null,
        allergies: profile.allergies || "",
        chronic_conditions: profile.chronic_conditions || "",
        family_history: profile.family_history || "",
        current_medications: profile.current_medications || "",
        notes: profile.notes || "",
      };
      const { data } = await api.patch(`/patients/${patient.id}/medical-profile`, payload);
      setProfile(data.medical_profile || payload);
      setEdit(false);
      onSaved && onSaved();
      toast.success("Ficha médica guardada");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="lg:sticky lg:top-4" data-testid="medical-profile-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-primary" /> Ficha médica
          </CardTitle>
          {!edit ? (
            <Button size="sm" variant="ghost" onClick={() => setEdit(true)} data-testid="profile-edit-btn">
              <Edit3 className="w-3.5 h-3.5" />
            </Button>
          ) : (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => setEdit(false)}>Cancelar</Button>
              <Button size="sm" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="profile-save-btn">
                <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "..." : "Guardar"}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="text-sm">
        <div className="border-t border-border pt-2 mt-2 first:border-t-0 first:pt-0 first:mt-0">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-1">
            <Heart className="w-3.5 h-3.5" /> Grupo sanguíneo
          </div>
          {edit ? (
            <Select value={profile.blood_type || ""} onValueChange={(v) => setProfile({ ...profile, blood_type: v })}>
              <SelectTrigger data-testid="profile-blood-type"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent>
                {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "desconocido"].map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="text-sm">{profile.blood_type
              ? <span className="inline-block px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 font-mono text-sm font-semibold" data-testid="profile-view-blood">{profile.blood_type}</span>
              : <span className="text-muted-foreground italic">—</span>}</div>
          )}
        </div>
        <ProfileField icon={AlertTriangle} label="Alergias" field="allergies" testid="allergies" edit={edit} profile={profile} setProfile={setProfile} />
        <ProfileField icon={Activity} label="Enfermedades crónicas" field="chronic_conditions" testid="chronic" edit={edit} profile={profile} setProfile={setProfile} />
        <ProfileField icon={UsersIcon} label="Antecedentes familiares" field="family_history" testid="family" edit={edit} profile={profile} setProfile={setProfile} />
        <ProfileField icon={Pill} label="Medicación actual" field="current_medications" testid="meds" edit={edit} profile={profile} setProfile={setProfile} />
        <ProfileField icon={FileText} label="Notas adicionales" field="notes" testid="notes" edit={edit} profile={profile} setProfile={setProfile} />
        {profile.updated_at && (
          <div className="text-[10px] text-muted-foreground mt-3 text-right">
            Actualizado {new Date(profile.updated_at).toLocaleDateString()}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ============ New entry dialog (with attachments + BMI) ============ */
function NewEntryDialog({ open, onOpenChange, patientId, onSaved }) {
  const [form, setForm] = useState({ title: "", type: "consultation" });
  const [vitals, setVitals] = useState({});
  const [soap, setSoap] = useState({ subjective: "", objective: "", assessment: "", plan: "" });
  const [icd10, setIcd10] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setForm({ title: "", type: "consultation" });
    setVitals({}); setSoap({ subjective: "", objective: "", assessment: "", plan: "" });
    setIcd10([]); setAttachments([]);
  };

  const submit = async () => {
    if (!form.title) { toast.error("Título requerido"); return; }
    setSaving(true);
    try {
      const content = [
        soap.subjective && `S: ${soap.subjective}`,
        soap.objective && `O: ${soap.objective}`,
        soap.assessment && `A: ${soap.assessment}`,
        soap.plan && `P: ${soap.plan}`,
      ].filter(Boolean).join("\n\n");
      const { data } = await api.post("/history", {
        patient_id: patientId, title: form.title, type: form.type, content: content || form.title,
        notes: content || form.title,
        vital_signs: Object.values(vitals).some((v) => v != null) ? vitals : null,
        soap: Object.values(soap).some((v) => v) ? soap : null,
        icd10,
      });
      // Attach files if any (POST /history/{id}/attachments with file_ids)
      if (attachments.length && data?.id) {
        try {
          await api.post(`/history/${data.id}/attachments`, {
            file_ids: attachments.map((a) => a.id),
          });
        } catch (e) { toast.error(`Adjuntos: ${formatError(e)}`); }
      }
      toast.success("Entrada guardada");
      onOpenChange(false);
      onSaved && onSaved();
      reset();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-entry-dialog">
        <DialogHeader>
          <DialogTitle>Nueva entrada de historia clínica</DialogTitle>
          <DialogDescription>
            Registra signos vitales, notas SOAP, códigos CIE-10 y adjuntos para la consulta actual.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2"><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="entry-title" /></div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="entry-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="consultation">Consulta</SelectItem>
                  <SelectItem value="lab">Resultado laboratorio</SelectItem>
                  <SelectItem value="note">Nota</SelectItem>
                  <SelectItem value="allergy">Alergia</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-sm font-semibold flex items-center gap-1"><Activity className="w-4 h-4" /> Signos vitales</Label>
            <div className="mt-2"><VitalsForm vitals={vitals} onChange={setVitals} /></div>
          </div>
          <div>
            <Label className="text-sm font-semibold">Notas SOAP</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Textarea placeholder="S — Subjetivo (síntomas descritos)" value={soap.subjective} onChange={(e) => setSoap({ ...soap, subjective: e.target.value })} rows={2} data-testid="soap-s" />
              <Textarea placeholder="O — Objetivo (exploración)" value={soap.objective} onChange={(e) => setSoap({ ...soap, objective: e.target.value })} rows={2} data-testid="soap-o" />
              <Textarea placeholder="A — Análisis (juicio clínico)" value={soap.assessment} onChange={(e) => setSoap({ ...soap, assessment: e.target.value })} rows={2} data-testid="soap-a" />
              <Textarea placeholder="P — Plan (tratamiento y seguimiento)" value={soap.plan} onChange={(e) => setSoap({ ...soap, plan: e.target.value })} rows={2} data-testid="soap-p" />
            </div>
          </div>
          <div>
            <Label className="text-sm font-semibold">Diagnósticos (CIE-10)</Label>
            <ICD10Picker selected={icd10} onChange={setIcd10} />
          </div>
          <div>
            <Label className="text-sm font-semibold">Adjuntos (imágenes, PDFs, resultados)</Label>
            <div className="mt-2">
              <FileUploader files={attachments} setFiles={setAttachments} purpose="history" testid="entry-attach" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="entry-submit">
            {saving ? "..." : "Guardar entrada"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============ Main page with timeline + search + side panel ============ */
export default function ClinicalHistory() {
  const location = useLocation();
  const [patients, setPatients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [reports, setReports] = useState([]);
  const [newDlg, setNewDlg] = useState(false);
  const [sending, setSending] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/patients")
      .then(({ data }) => {
        setPatients(data || []);
        // Preselect from router location state (e.g. arriving from /app/pacientes)
        const requestedId = location.state?.patientId;
        if (requestedId) {
          const match = (data || []).find((p) => p.id === requestedId);
          if (match) setSelected(match);
        }
      })
      .catch((e) => toast.error(`No se pudieron cargar los pacientes: ${formatError(e)}`));
  }, []);
  useEffect(() => {
    if (selected) {
      api.get(`/history/${selected.id}`)
        .then(({ data }) => setHistory(data || []))
        .catch(() => setHistory([]));
      api.get(`/medical-reports?patient_id=${selected.id}`)
        .then(({ data }) => setReports(data || []))
        .catch(() => setReports([]));
    } else { setHistory([]); setReports([]); }
  }, [selected, newDlg]);

  const filteredPatients = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.email || "").toLowerCase().includes(q) ||
      (p.dni || "").toLowerCase().includes(q) ||
      (p.phone || "").toLowerCase().includes(q)
    );
  }, [patients, query]);

  const sendByEmail = async () => {
    if (!window.confirm(`¿Enviar la historia clínica completa por email a ${selected.email}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post(`/history/${selected.id}/send-email`);
      if (data.ok && data.email_id) {
        toast.success(`Historia enviada · ${data.entries} entradas, ${data.prescriptions} recetas, ${data.exams} exámenes`);
      } else {
        toast.error("El proveedor de email rechazó el envío.");
      }
    } catch (e) { toast.error(formatError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="clinical-history-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display text-3xl flex items-center gap-2"><FileText className="w-7 h-7 text-emerald-700" /> Historia Clínica</h1>
        <Button asChild variant="outline" data-testid="go-patients-btn">
          <Link to="/app/pacientes"><UsersRound className="w-4 h-4 mr-1" /> Añadir paciente</Link>
        </Button>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr_320px] gap-4">
        {/* LEFT — patient list with search */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Pacientes ({filteredPatients.length})</CardTitle>
            <div className="relative mt-1">
              <Search className="w-4 h-4 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nombre, email, DNI…"
                className="pl-8 h-9"
                data-testid="patient-search"
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-1 max-h-[600px] overflow-y-auto">
            {filteredPatients.map((p) => (
              <button key={p.id} onClick={() => setSelected(p)} className={`w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-muted ${selected?.id === p.id ? "bg-emerald-100 text-emerald-900" : ""}`} data-testid={`patient-${p.id}`}>
                <div className="font-medium truncate">{p.name}</div>
                <div className="text-xs text-muted-foreground truncate">{p.email}</div>
              </button>
            ))}
            {filteredPatients.length === 0 && (
              <div className="text-xs text-muted-foreground text-center py-4">Sin resultados</div>
            )}
          </CardContent>
        </Card>

        {/* CENTER — history timeline */}
        <div>
          {selected ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-display text-xl">{selected.name}</div>
                  <div className="text-sm text-muted-foreground">{selected.email}</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button asChild className="bg-primary hover:bg-primary/90" data-testid="start-in-person-btn">
                    <Link to={`/app/consulta-presencial/${selected.id}`}>
                      <Stethoscope className="w-4 h-4 mr-1" /> Iniciar consulta presencial
                    </Link>
                  </Button>
                  <Button asChild variant="outline" data-testid="issue-certificate-btn">
                    <Link to="/app/certificados">
                      <FileSignature className="w-4 h-4 mr-1" /> Emitir certificado
                    </Link>
                  </Button>
                  <Button asChild variant="outline" data-testid="new-report-btn">
                    <Link to="/app/informes" state={{ patientId: selected.id }}>
                      <FileCheck className="w-4 h-4 mr-1" /> Nuevo informe
                    </Link>
                  </Button>
                  <Button variant="outline" onClick={sendByEmail} disabled={sending} data-testid="send-history-btn"><Send className="w-4 h-4 mr-1" /> {sending ? "Enviando..." : "Enviar por email"}</Button>
                  <Button onClick={() => setNewDlg(true)} className="bg-emerald-700 hover:bg-emerald-800" data-testid="new-entry-btn"><Plus className="w-4 h-4 mr-1" /> Nueva entrada</Button>
                </div>
              </div>

              {reports.length > 0 && (
                <Card className="mb-4" data-testid="reports-section">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileCheck className="w-4 h-4 text-primary" /> Informes de consulta ({reports.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-2 space-y-2">
                    {reports.slice(0, 6).map((r) => (
                      <div key={r.id} className="flex items-center justify-between border border-border rounded-lg px-3 py-2" data-testid={`report-card-${r.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className="bg-teal-100 text-teal-700">Informe</Badge>
                            <span className="text-xs text-muted-foreground font-mono">{r.code}</span>
                            <span className="text-xs text-muted-foreground">· {new Date(r.report_date || r.created_at).toLocaleDateString("es-ES")}</span>
                          </div>
                          <div className="text-sm mt-0.5 truncate">{r.reason}</div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => generateMedicalReportPDF(r)} data-testid={`report-pdf-${r.id}`}>
                          <Download className="w-4 h-4 mr-1" /> PDF
                        </Button>
                      </div>
                    ))}
                    {reports.length > 6 && (
                      <Button asChild variant="ghost" size="sm" className="w-full">
                        <Link to="/app/informes">Ver todos los informes →</Link>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}

              {history.length === 0 ? (
                <div className="text-sm text-muted-foreground py-12 text-center border border-dashed rounded-lg" data-testid="history-empty">
                  Sin entradas todavía. Registra la primera consulta con «Nueva entrada».
                </div>
              ) : (
                <div className="relative pl-6 border-l-2 border-emerald-200 space-y-4" data-testid="history-timeline">
                  {history.map((h) => {
                    const bmi = h.vital_signs ? computeBMI(h.vital_signs.weight, h.vital_signs.height) : null;
                    const label = bmiLabel(bmi);
                    return (
                      <div key={h.id} className="relative" data-testid={`history-${h.id}`}>
                        {/* Timeline dot */}
                        <div className="absolute -left-[29px] w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-white shadow" />
                        <Card>
                          <CardContent className="p-4 space-y-2">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium">{h.title}</div>
                                <div className="text-xs text-muted-foreground">{(h.created_at || "").slice(0, 16).replace("T", " ")} · {h.doctor_name}</div>
                              </div>
                              <Badge variant="outline">{h.type}</Badge>
                            </div>
                            {h.vital_signs && Object.values(h.vital_signs).some((v) => v != null) && (
                              <div className="flex flex-wrap gap-2 text-xs mt-2">
                                {h.vital_signs.ta_sys && h.vital_signs.ta_dia && <span className="px-2 py-0.5 bg-slate-100 rounded">TA {h.vital_signs.ta_sys}/{h.vital_signs.ta_dia}</span>}
                                {h.vital_signs.fc && <span className="px-2 py-0.5 bg-slate-100 rounded">FC {h.vital_signs.fc}</span>}
                                {h.vital_signs.temp && <span className="px-2 py-0.5 bg-slate-100 rounded">T {h.vital_signs.temp}°C</span>}
                                {h.vital_signs.sat_o2 && <span className="px-2 py-0.5 bg-slate-100 rounded">SatO₂ {h.vital_signs.sat_o2}%</span>}
                                {h.vital_signs.weight && <span className="px-2 py-0.5 bg-slate-100 rounded">{h.vital_signs.weight}kg</span>}
                                {h.vital_signs.height && <span className="px-2 py-0.5 bg-slate-100 rounded">{h.vital_signs.height}cm</span>}
                                {bmi != null && <span className={`px-2 py-0.5 rounded border ${label.color}`}>IMC {bmi.toFixed(1)}</span>}
                              </div>
                            )}
                            {h.icd10 && h.icd10.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {h.icd10.map((c) => <span key={c} className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded font-mono">{c}</span>)}
                              </div>
                            )}
                            <div className="text-sm text-foreground/80 whitespace-pre-wrap mt-2">{h.content || h.notes}</div>
                            {h.attachments && h.attachments.length > 0 && (
                              <div className="mt-2">
                                <div className="text-xs text-muted-foreground mb-1">Adjuntos ({h.attachments.length})</div>
                                <AttachmentList items={h.attachments} />
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16 text-muted-foreground border border-dashed rounded-lg">Selecciona un paciente para ver su historia</div>
          )}
        </div>

        {/* RIGHT — persistent medical profile side panel */}
        <div>
          {selected ? (
            <MedicalProfilePanel patient={selected} onSaved={() => {}} />
          ) : (
            <Card><CardContent className="pt-6 text-sm text-muted-foreground text-center">Selecciona un paciente para ver su ficha médica</CardContent></Card>
          )}
        </div>
      </div>

      <NewEntryDialog open={newDlg} onOpenChange={setNewDlg} patientId={selected?.id} onSaved={() => {}} />
    </div>
  );
}
